import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { knowledgeAPI, capsulesAPI, agentsAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  FolderSearch, HardDrive, FileText, Search, Loader2,
  RefreshCw, Database, Brain, Play, ChevronRight, Clock,
  FileCode, FileType, FolderOpen, Ticket, AlertCircle, CheckCircle2,
  GitBranch, Zap, ArrowRight, Network, ShieldCheck, Eye,
  Hash, Box, ArrowRightLeft, HelpCircle, FileUp, SlidersHorizontal, BookOpen, Link2,
} from 'lucide-react';
import GraphVisualization from '@/components/GraphVisualization';
import KBHelpPanel from '@/components/KBHelpPanel';
import KBContentPanel from '@/components/kb/KBContentPanel';
import KBOriginsPanel from '@/components/kb/KBOriginsPanel';
import OrgMemoryObservatoryPanel from '@/components/kb/OrgMemoryObservatoryPanel';
import { fechaYHora } from '../lib/fechas';

function StatCard({ label, value, icon: Icon, color = 'text-brand-primary' }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-sm border border-brand-border bg-white" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="p-2 rounded-sm" style={{ backgroundColor: `${color === 'text-brand-primary' ? '#002FA710' : color === 'text-brand-success' ? '#00D15C10' : color === 'text-purple-500' ? '#9333EA10' : '#FFB80010'}` }}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div>
        <p className="text-xs text-zinc-500">{label}</p>
        <p className="text-lg font-heading font-bold text-[#09090B]">{value}</p>
      </div>
    </div>
  );
}

function KbInlineHelp({ children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[11px] text-brand-primary hover:text-[#001F6D] transition-colors"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        <span className="font-medium">{open ? 'Ocultar ayuda' : 'Como funciona esta seccion?'}</span>
      </button>
      {open && (
        <div className="mt-2 p-3 bg-blue-50/60 border border-blue-200/60 rounded-sm text-[11px] text-zinc-700 leading-relaxed space-y-1.5">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Source Config Panel ──
function SourceConfigPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const res = await knowledgeAPI.getSourceConfig();
      setConfig(res.data);
    } catch (err) { toast.error('Error cargando config'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await knowledgeAPI.updateSourceConfig({
        source_path: config.source_path || '',
        enabled: config.enabled || false,
        scan_interval_minutes: config.scan_interval_minutes || 10,
      });
      toast.success('Configuracion guardada');
    } catch (err) { toast.error('Error guardando'); }
    finally { setSaving(false); }
  };

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await knowledgeAPI.scan({});
      setScanResult(res.data);
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        const graphInfo = res.data.graph_stats?.nodes_created
          ? ` | Grafo: ${res.data.graph_stats.nodes_created} nodos, ${res.data.graph_stats.edges_created} edges`
          : '';
        toast.success(`Escaneo completo: ${res.data.files_indexed} indexados, ${res.data.files_unchanged} sin cambios${graphInfo}`);
      }
    } catch (err) { toast.error('Error escaneando'); }
    finally { setScanning(false); }
  };

  if (loading) return <Skeleton className="h-[200px]" />;

  return (
    <Card className="border border-brand-border rounded-sm" data-testid="source-config-panel">
      <CardHeader className="pb-2 pt-4 px-5">
        <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-1.5">
          <HardDrive className="w-3.5 h-3.5" /> Origen de Datos (Unidad Montada)
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5 space-y-4">
        <p className="text-[11px] text-zinc-500">
          Configura la ruta a la carpeta montada con los fuentes, documentacion y artefactos. OMNISYS escaneara automaticamente, construira la base de conocimiento y el grafo de dependencias.
          La ruta se guarda en MongoDB (<span className="font-mono">file_watcher_config</span>); debe coincidir con el directorio real dentro del backend (en Docker suele ser <span className="font-mono">/data/sources</span>, volumen desde <span className="font-mono">OMNISYS_SOURCES_PATH</span>). Los vectores en Qdrant y la pestaña Archivos indexados pueden seguir mostrando datos aunque el Explorador quede vacio si esta ruta apunta a un path inexistente.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Ruta de la Unidad</Label>
            <Input
              data-testid="source-path-input"
              value={config?.source_path || ''}
              onChange={e => setConfig(p => ({ ...p, source_path: e.target.value }))}
              placeholder="/data/sources"
              className="rounded-sm text-xs font-mono h-9"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Intervalo de Escaneo (min)</Label>
            <Input
              data-testid="scan-interval-input"
              type="number"
              min={1}
              value={config?.scan_interval_minutes || 10}
              onChange={e => setConfig(p => ({ ...p, scan_interval_minutes: parseInt(e.target.value) || 10 }))}
              className="rounded-sm text-xs h-9"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            data-testid="auto-scan-toggle"
            checked={config?.enabled || false}
            onCheckedChange={v => setConfig(p => ({ ...p, enabled: v }))}
          />
          <Label className="text-xs text-zinc-600">Escaneo automatico habilitado</Label>
        </div>

        <div className="flex gap-2">
          <Button data-testid="save-source-config" onClick={handleSave} disabled={saving} size="sm" className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-8">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Guardar
          </Button>
          <Button data-testid="trigger-scan-btn" onClick={handleScan} disabled={scanning} variant="outline" size="sm" className="rounded-sm text-xs h-8 gap-1">
            {scanning ? <><Loader2 className="w-3 h-3 animate-spin" /> Escaneando...</> : <><RefreshCw className="w-3 h-3" /> Escanear Ahora</>}
          </Button>
        </div>

        {config?.last_scan && (
          <div className="flex items-center gap-1.5 text-[10px] text-zinc-400">
            <Clock className="w-3 h-3" />
            Ultimo escaneo: {fechaYHora(config.last_scan)}
            {config.last_scan_result && (
              <span className="ml-2 text-zinc-500">
                ({config.last_scan_result.files_indexed} indexados, {config.last_scan_result.files_unchanged} sin cambios)
              </span>
            )}
          </div>
        )}

        {scanResult && !scanResult.error && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-sm text-xs space-y-1" data-testid="scan-result">
            <p className="font-semibold text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Escaneo completo</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-green-600">
              <span>Encontrados: <b>{scanResult.files_found}</b></span>
              <span>Indexados: <b>{scanResult.files_indexed}</b></span>
              <span>Sin cambios: <b>{scanResult.files_unchanged}</b></span>
              <span>Omitidos: <b>{scanResult.files_skipped}</b></span>
              <span>Errores: <b>{scanResult.files_error}</b></span>
            </div>
            {scanResult.graph_stats?.nodes_created > 0 && (
              <div className="mt-1 flex items-center gap-1.5 text-purple-600">
                <GitBranch className="w-3 h-3" />
                Grafo: <b>{scanResult.graph_stats.nodes_created}</b> nodos, <b>{scanResult.graph_stats.edges_created}</b> edges
                {scanResult.graph_stats.languages && Object.keys(scanResult.graph_stats.languages).length > 0 && (
                  <span className="text-purple-400 ml-1">
                    ({Object.entries(scanResult.graph_stats.languages).map(([l, c]) => `${l}: ${c}`).join(', ')})
                  </span>
                )}
              </div>
            )}
            {scanResult.errors?.length > 0 && (
              <div className="mt-1 text-red-600">
                {scanResult.errors.map((e, i) => <p key={i} className="text-[10px]">{e.file}: {e.error}</p>)}
              </div>
            )}
          </div>
        )}

        <div className="flex items-start gap-2 p-2 bg-zinc-50 border border-zinc-200 rounded-sm">
          <AlertCircle className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0 mt-0.5" />
          <div className="text-[10px] text-zinc-500">
            <p><b>Estructura esperada:</b></p>
            <pre className="mt-1 font-mono text-[9px] leading-relaxed">{`/data/sources/
├── sgestion/           → indexa como "code"
│   ├── PKG_INVENTARIO.bdy
│   └── PKG_VENTAS.spc
├── docs/               → indexa como "docs"
│   └── manual.md
└── Tickets/            → indexa como "tickets"
    ├── TK-001/
    │   └── reporte.md
    └── TK-002/
        └── analisis.sql`}</pre>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Indexed Files Panel ──
function IndexedFilesPanel() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await knowledgeAPI.files();
        setFiles(res.data.files || []);
      } catch { /* no files yet */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const filtered = files.filter(f =>
    !filter || f.rel_path.toLowerCase().includes(filter.toLowerCase()) || f.collection_type.includes(filter.toLowerCase())
  );

  const folders = {};
  filtered.forEach(f => {
    const dir = f.rel_path.includes('/') ? f.rel_path.split('/').slice(0, -1).join('/') : '(raiz)';
    if (!folders[dir]) folders[dir] = [];
    folders[dir].push(f);
  });

  const colIcon = (t) => {
    if (t === 'code') return <FileCode className="w-3 h-3 text-blue-500" />;
    if (t === 'tickets') return <Ticket className="w-3 h-3 text-amber-500" />;
    return <FileType className="w-3 h-3 text-green-500" />;
  };

  const colColor = (t) => {
    if (t === 'code') return 'bg-blue-100 text-blue-700';
    if (t === 'tickets') return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
  };

  if (loading) return <Skeleton className="h-[300px]" />;

  return (
    <div data-testid="indexed-files-panel">
      <div className="flex items-center gap-2 mb-3">
        <Input
          data-testid="files-filter"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filtrar archivos..."
          className="flex-1 rounded-sm text-xs h-8"
        />
        <Badge variant="outline" className="text-[10px] rounded-sm">{filtered.length} archivos</Badge>
      </div>

      {Object.keys(folders).length === 0 ? (
        <div className="h-[200px] flex flex-col items-center justify-center text-zinc-400">
          <FolderSearch className="w-8 h-8 mb-2 opacity-30" />
          <p className="text-xs">No hay archivos indexados</p>
          <p className="text-[10px] mt-1">Configura la ruta y ejecuta un escaneo</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {Object.entries(folders).map(([dir, dirFiles]) => (
            <div key={dir}>
              <div className="flex items-center gap-1.5 mb-1">
                <FolderOpen className="w-3 h-3 text-zinc-400" />
                <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-[0.08em]">{dir}</span>
                <Badge variant="outline" className="text-[9px] rounded-sm ml-1">{dirFiles.length}</Badge>
              </div>
              <div className="space-y-1 ml-4">
                {dirFiles.map(f => (
                  <div key={f.rel_path} className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0" data-testid={`file-${f.filename}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {colIcon(f.collection_type)}
                      <span className="text-xs text-[#09090B] truncate">{f.filename}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge className={`text-[9px] rounded-sm border-0 ${colColor(f.collection_type)}`}>{f.collection_type}</Badge>
                      <span className="text-[10px] text-zinc-400 font-mono">{f.chunks_indexed}ch</span>
                      {f.ticket_id && <Badge variant="outline" className="text-[9px] rounded-sm">{f.ticket_id}</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Explorador de fuentes (listado real + indexacion selectiva) ──
function statusBadgeClass(st) {
  if (st === 'indexed') return 'bg-emerald-100 text-emerald-800 border-0';
  if (st === 'partial' || st === 'pending') return 'bg-amber-100 text-amber-800 border-0';
  if (st === 'unsupported') return 'bg-zinc-100 text-zinc-600 border-0';
  return 'bg-slate-100 text-slate-700 border-0';
}

function SourceExplorerPanel() {
  const [entries, setEntries] = useState([]);
  const [sourcePath, setSourcePath] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [activeJob, setActiveJob] = useState(null);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await knowledgeAPI.sourceTree();
      const d = res.data || {};
      if (d.error) {
        toast.error(d.error);
        setEntries([]);
        setSourcePath(d.source_path || '');
        return;
      }
      setEntries(d.entries || []);
      setSourcePath(d.source_path || '');
      setTruncated(!!d.truncated);
    } catch {
      toast.error('No se pudo cargar el explorador de fuentes');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const loadActiveJob = useCallback(async () => {
    try {
      const res = await knowledgeAPI.getActiveIndexJob();
      setActiveJob(res.data?.active_job || null);
    } catch {
      setActiveJob(null);
    }
  }, []);

  useEffect(() => {
    loadActiveJob();
  }, [loadActiveJob]);

  useEffect(() => {
    if (!activeJob?.job_id) return;
    const iv = setInterval(async () => {
      try {
        const res = await knowledgeAPI.getIndexJob(activeJob.job_id);
        const j = res.data;
        if (['completed', 'failed', 'cancelled'].includes(j.status)) {
          setActiveJob(null);
          clearInterval(iv);
          await loadTree();
          await loadActiveJob();
          if (j.status === 'completed') toast.success('Indexacion full+grafo completada');
          else if (j.status === 'cancelled') toast('Indexacion cancelada');
          else toast.error('Indexacion finalizo con error');
        } else {
          setActiveJob(j);
        }
      } catch {
        clearInterval(iv);
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [activeJob?.job_id, loadTree, loadActiveJob]);

  const toggleSel = (path) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(path)) n.delete(path);
      else n.add(path);
      return n;
    });
  };

  const filtered = entries.filter((e) =>
    !filter || e.path.toLowerCase().includes(filter.toLowerCase()),
  );

  const runScan = async (payload) => {
    setScanning(true);
    try {
      const res = await knowledgeAPI.scan(payload);
      const d = res.data || {};
      if (d.error) {
        toast.error(d.error);
        return;
      }
      toast.success(
        d.scan_scope === 'targeted'
          ? `Indexacion selectiva: ${d.files_indexed} indexados, ${d.files_unchanged} sin cambios`
          : `Escaneo completo: ${d.files_indexed} indexados, ${d.files_unchanged} sin cambios`,
      );
      setSelected(new Set());
      await loadTree();
    } catch {
      toast.error('Error en escaneo');
    } finally {
      setScanning(false);
    }
  };

  if (loading) return <Skeleton className="h-[360px]" />;

  return (
    <div className="space-y-3" data-testid="source-explorer-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-[10px] font-mono max-w-[70vw] truncate" title={sourcePath}>
          {sourcePath || '(sin source_path)'}
        </Badge>
        {truncated && (
          <Badge variant="outline" className="text-[10px] text-amber-700">
            Listado truncado (limite de seguridad)
          </Badge>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs h-8"
          onClick={() => loadTree()}
          disabled={scanning}
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${scanning ? 'animate-spin' : ''}`} />
          Actualizar vista
        </Button>
        <Button
          type="button"
          size="sm"
          className="text-xs h-8 bg-brand-primary text-white"
          disabled={scanning || !!activeJob || selected.size === 0}
          onClick={() =>
            runScan({ target_paths: Array.from(selected), rebuild_graph: false })
          }
        >
          Indexar seleccion ({selected.size})
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="text-xs h-8"
          disabled={scanning || !!activeJob}
          onClick={async () => {
            try {
              const res = await knowledgeAPI.startIndexJob();
              if (!res.data?.ok && res.data?.error === 'job_already_running') {
                setActiveJob(res.data?.job || null);
                toast('Ya existe una indexacion activa');
              } else if (res.data?.ok) {
                toast.success('Indexacion full+grafo iniciada en background');
                await loadActiveJob();
              }
            } catch {
              toast.error('No se pudo iniciar indexacion background');
            }
          }}
        >
          Indexar todo + grafo (background)
        </Button>
        {activeJob?.job_id && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="text-xs h-8"
            onClick={async () => {
              try {
                await knowledgeAPI.cancelIndexJob(activeJob.job_id);
                toast('Cancelacion solicitada');
              } catch {
                toast.error('No se pudo cancelar');
              }
            }}
          >
            Cancelar indexacion
          </Button>
        )}
      </div>
      {activeJob?.job_id && (
        <div className="p-2 border border-indigo-200 bg-indigo-50 rounded-sm text-xs text-indigo-700">
          <div className="flex items-center justify-between">
            <span>Indexador activo: {activeJob.phase} ({activeJob.status})</span>
            <span className="font-mono">{activeJob.progress_pct || 0}%</span>
          </div>
          <div className="h-1.5 bg-indigo-100 rounded mt-2 overflow-hidden">
            <div className="h-full bg-indigo-500" style={{ width: `${activeJob.progress_pct || 0}%` }} />
          </div>
        </div>
      )}
      <Input
        placeholder="Filtrar por ruta..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="text-xs h-8 rounded-sm max-w-md"
      />
      <div className="border border-brand-border rounded-sm max-h-[480px] overflow-auto">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-xs text-zinc-500">
            No hay archivos listables o falta configurar la ruta de origen (pestaña Origen de Datos).
          </div>
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="p-2 w-8" />
                <th className="p-2 font-semibold text-zinc-600">Archivo</th>
                <th className="p-2 font-semibold text-zinc-600 w-32">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.path} className="border-b border-zinc-100 hover:bg-zinc-50/80">
                  <td className="p-1.5 align-middle">
                    {e.index_status !== 'unsupported' ? (
                      <input
                        type="checkbox"
                        className="rounded border-zinc-300"
                        checked={selected.has(e.path)}
                        onChange={() => toggleSel(e.path)}
                        aria-label={`Seleccionar ${e.path}`}
                      />
                    ) : null}
                  </td>
                  <td className="p-2 font-mono text-[11px] text-[#09090B] break-all">{e.path}</td>
                  <td className="p-2">
                    <Badge className={`text-[9px] rounded-sm ${statusBadgeClass(e.index_status)}`}>
                      {e.index_status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Playground Panel ──
function PlaygroundPanel({ user, seedCapsule = '', seedQuery = '' }) {
  const [query, setQuery] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [deepAnalysis, setDeepAnalysis] = useState(false);
  const [capsules, setCapsules] = useState([]);
  const [agents, setAgents] = useState([]);
  const [capsuleId, setCapsuleId] = useState(user?.capsule_id || '');
  const [agentId, setAgentId] = useState('');
  const [tenantMounts, setTenantMounts] = useState([]);
  const [capsuleSourceAccess, setCapsuleSourceAccess] = useState([]);

  useEffect(() => {
    if (seedQuery) setQuery(seedQuery);
  }, [seedQuery]);

  useEffect(() => {
    if (seedCapsule) setCapsuleId(seedCapsule);
  }, [seedCapsule]);

  const loadCapsules = useCallback(async () => {
    if (!user?.tenant_id) return;
    const res = await capsulesAPI.list(user.tenant_id);
    const list = (res.data || []).filter((c) => c?.status !== 'archived');
    setCapsules(list);
    if (!list.length) {
      setCapsuleId('');
      return;
    }
    const hasCurrent = list.some((c) => c.capsule_id === capsuleId);
    if (hasCurrent) return;
    const preferred = list.find((c) => c.capsule_id === (user?.capsule_id || ''));
    setCapsuleId((preferred || list[0]).capsule_id);
  }, [user?.tenant_id, capsuleId]);

  const loadAgents = useCallback(async () => {
    if (!user?.tenant_id || !capsuleId) return;
    try {
      const res = await agentsAPI.list(user.tenant_id, capsuleId);
      const list = res.data || [];
      setAgents(list);
      const hasCurrent = list.some((a) => a.agent_id === agentId);
      if (!hasCurrent) {
        setAgentId(list.length > 0 ? list[0].agent_id : '');
      }
    } catch (err) {
      setAgents([]);
      setAgentId('');
      toast.error(`No se pudieron cargar agentes de la capsula: ${formatApiError(err)}`);
    }
  }, [user?.tenant_id, capsuleId, agentId]);

  useEffect(() => {
    loadCapsules().catch(() => {});
  }, [loadCapsules]);

  useEffect(() => {
    loadAgents().catch(() => {});
  }, [loadAgents]);

  const loadScopeHints = useCallback(async () => {
    try {
      const mountsRes = await knowledgeAPI.listSourceMounts();
      setTenantMounts((mountsRes.data?.mounts || []).filter((m) => m.enabled !== false));
    } catch {
      setTenantMounts([]);
    }
    if (!capsuleId) {
      setCapsuleSourceAccess([]);
      return;
    }
    try {
      const accRes = await knowledgeAPI.getCapsuleSourceAccess(capsuleId);
      setCapsuleSourceAccess(accRes.data?.kb_source_access || []);
    } catch {
      setCapsuleSourceAccess([]);
    }
  }, [capsuleId]);

  useEffect(() => {
    loadScopeHints().catch(() => {});
  }, [loadScopeHints]);

  const scopeMissingMounts = tenantMounts.length > 0 && capsuleSourceAccess.length === 0;

  const handleRun = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    if (!capsuleId) {
      toast.error('Selecciona una capsula para ejecutar Playground');
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await knowledgeAPI.playground({
        query: query.trim(),
        max_results: 5,
        rerank: true,
        deep_analysis: deepAnalysis,
        capsule_id: capsuleId || user?.capsule_id || '',
        agent_id: agentId,
      });
      setResult(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="playground-panel" className="space-y-4">
      <div className="flex items-start gap-2 p-2 bg-purple-50 border border-purple-200 rounded-sm">
        <Brain className="w-3.5 h-3.5 text-purple-500 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-purple-700">
          Prueba la base de conocimiento con el scope efectivo de la capsula y agente seleccionados
          (Config Capsula → Conocimiento y Agentes → Runtime). Recupera fragmentos RAG filtrados y responde con el LLM.
        </p>
      </div>

      {scopeMissingMounts && (
        <div
          className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-sm"
          data-testid="playground-scope-cta"
        >
          <Link2 className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-[10px] text-amber-900 min-w-0">
            <p className="font-semibold">Hay {tenantMounts.length} mount(s) indexados pero esta cápsula no está suscrita</p>
            <p className="text-amber-800 mt-0.5">
              Sin <code className="text-[9px]">kb_source_access</code> el Playground no recuperará esos archivos.
              Configuralo en Config cápsula → Conocimiento.
            </p>
            <Link
              to={`/capsule-config?tab=kb&capsule=${encodeURIComponent(capsuleId)}`}
              className="inline-block mt-1 text-amber-700 underline font-medium hover:text-amber-900"
            >
              Suscribir mounts para {capsuleId}
            </Link>
          </div>
        </div>
      )}

      <form onSubmit={handleRun} className="space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <select
            className="h-8 text-xs rounded-sm border border-zinc-200 px-2"
            value={capsuleId}
            onChange={(e) => { setCapsuleId(e.target.value); setAgentId(''); }}
          >
            {capsules.map((c) => <option key={c.capsule_id} value={c.capsule_id}>{c.capsule_id}</option>)}
          </select>
          <select
            className="h-8 text-xs rounded-sm border border-zinc-200 px-2"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            <option value="">(auto en capsula)</option>
            {agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.agent_id}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <Textarea
            data-testid="playground-query"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Ej: Como se calcula el stock disponible de un articulo?"
            className="flex-1 rounded-sm text-sm min-h-[60px] resize-none"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleRun(); } }}
          />
          <Button
            data-testid="playground-run-btn"
            type="submit"
            disabled={running || !query.trim() || !capsuleId}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm h-[60px] w-[60px]"
          >
            {running ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
          </Button>
        </div>
        <div className="flex items-center gap-3 px-1">
          <Switch
            data-testid="deep-analysis-toggle"
            checked={deepAnalysis}
            onCheckedChange={setDeepAnalysis}
          />
          <Label className="text-xs text-zinc-600 flex items-center gap-1.5 cursor-pointer" onClick={() => setDeepAnalysis(!deepAnalysis)}>
            <GitBranch className="w-3 h-3 text-purple-500" />
            Analisis Profundo
            <span className="text-[10px] text-zinc-400">(Multi-step RAG + Grafo de Dependencias)</span>
          </Label>
        </div>
      </form>

      {result && (
        <div className="space-y-4">
          {result.kb_scope && (
            <div
              className={`p-2 rounded-sm text-[10px] border ${
                result.kb_scope.scope_empty
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-zinc-50 border-zinc-200 text-zinc-600'
              }`}
              data-testid="playground-kb-scope"
            >
              <span className="font-semibold">Scope KB: </span>
              capsula {result.kb_scope.capsule_id}, agente {result.kb_scope.agent_id}
              {result.kb_scope.repo_ids?.length > 0 && (
                <span> · repos: {result.kb_scope.repo_ids.join(', ')}</span>
              )}
              {result.kb_scope.mount_ids?.length > 0 && (
                <span> · mounts: {result.kb_scope.mount_ids.join(', ')}</span>
              )}
              {result.kb_scope.scope_empty && (
                <span className="block mt-1">
                  Sin repos ni mounts asignados a esta capsula/agente. Asignalos en Config Capsula → Conocimiento.
                </span>
              )}
            </div>
          )}
          {/* LLM Answer */}
          <Card className="border-purple-200 rounded-sm bg-purple-50/30" data-testid="playground-llm-answer">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-purple-600 flex items-center gap-1.5">
                <Brain className="w-3 h-3" /> Respuesta del LLM
                {result.llm_model && <Badge className="text-[8px] rounded-sm bg-purple-200 text-purple-700 border-0 ml-2">{result.llm_model}</Badge>}
                {(result.llm_input_tokens > 0 || result.llm_output_tokens > 0 || result.llm_tokens > 0) && (
                  <span className="text-[9px] text-purple-400 ml-1 font-mono" data-testid="playground-token-badge">
                    {result.llm_input_tokens != null || result.llm_output_tokens != null
                      ? `in: ${(result.llm_input_tokens || 0).toLocaleString('es-AR')} · out: ${(result.llm_output_tokens || 0).toLocaleString('es-AR')} · total: ${(result.llm_tokens || 0).toLocaleString('es-AR')}`
                      : `${result.llm_tokens} tokens`}
                  </span>
                )}
                {result.deep_analysis && <Badge className="text-[8px] rounded-sm bg-indigo-200 text-indigo-700 border-0 ml-1">Deep</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="text-sm text-[#09090B] whitespace-pre-wrap leading-relaxed" data-testid="llm-answer-text">
                {result.llm_answer}
              </div>
            </CardContent>
          </Card>

          {/* Graph Flow (if deep analysis) */}
          {result.graph_flow && (
            <Card className="border-indigo-200 rounded-sm bg-indigo-50/30" data-testid="graph-flow-card">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-indigo-600 flex items-center gap-1.5">
                  <GitBranch className="w-3 h-3" /> Flujo de Dependencias
                  {result.objects_explored?.length > 0 && (
                    <span className="text-[9px] text-indigo-400 ml-1">{result.objects_explored.length} objetos explorados</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <pre className="text-[11px] text-indigo-800 whitespace-pre-wrap font-mono bg-indigo-50 p-3 rounded-sm max-h-[250px] overflow-y-auto">
                  {result.graph_flow}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Retrieved Chunks */}
          <div>
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Search className="w-3 h-3" /> Fragmentos Recuperados ({result.results_count})
            </h3>
            {result.results?.length === 0 ? (
              <div className="p-4 border border-dashed border-zinc-200 rounded-sm text-center text-xs text-zinc-400">
                {result.kb_scope?.scope_empty
                  ? 'Sin resultados: no hay orígenes KB asignados a esta capsula/agente.'
                  : 'No se encontraron fragmentos relevantes en el scope configurado. Verifica indexacion y asignaciones.'}
              </div>
            ) : (
              <div className="space-y-2">
                {result.results?.map((r, idx) => (
                  <Card key={idx} className={`border rounded-sm ${r.source === 'graph' ? 'border-indigo-200' : 'border-zinc-200'}`} data-testid={`chunk-result-${idx}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[9px] rounded-sm border-0 ${r.source === 'graph' ? 'bg-indigo-100 text-indigo-600' : 'bg-zinc-100 text-zinc-600'}`}>
                            {r.source === 'graph' ? 'Grafo' : `Fuente ${idx + 1}`}
                          </Badge>
                          <span className="text-xs font-medium text-[#09090B]">{r.filename}</span>
                          <Badge variant="outline" className="text-[9px] rounded-sm capitalize">{r.collection}/{r.object_type || r.doc_type || ''}</Badge>
                          {r.related_to && (
                            <span className="text-[9px] text-indigo-500 flex items-center gap-0.5">
                              <ArrowRight className="w-2.5 h-2.5" /> {r.related_to}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {r.rerank_position && <Badge className="text-[9px] rounded-sm bg-purple-100 text-purple-600 border-0">#{r.rerank_position}</Badge>}
                          <span className="text-[10px] font-mono text-brand-primary">{(r.score * 100).toFixed(1)}%</span>
                          {r.original_score != null && r.original_score !== r.score && (
                            <span className="text-[10px] font-mono text-zinc-400">(vec: {(r.original_score * 100).toFixed(1)}%)</span>
                          )}
                        </div>
                      </div>
                      <pre className="text-[11px] text-zinc-600 whitespace-pre-wrap font-mono bg-zinc-50 p-2 rounded-sm max-h-[200px] overflow-y-auto">
                        {r.chunk_content || r.content_preview}
                      </pre>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dependency Graph Panel ──
function GraphPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [extractingDocs, setExtractingDocs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [deps, setDeps] = useState(null);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [showViz, setShowViz] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await knowledgeAPI.graphStats();
      setStats(res.data);
    } catch { /* no graph yet */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleBuild = async () => {
    setBuilding(true);
    try {
      const res = await knowledgeAPI.graphBuild();
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(`Grafo construido: ${res.data.stats.nodes_created} nodos, ${res.data.stats.edges_created} edges`);
        loadStats();
      }
    } catch (err) { toast.error('Error construyendo grafo'); }
    finally { setBuilding(false); }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults(null);
    setSelectedNode(null);
    setDeps(null);
    try {
      const res = await knowledgeAPI.graphSearch(searchQuery.trim());
      setSearchResults(res.data.results || []);
    } catch { toast.error('Error buscando'); }
    finally { setSearching(false); }
  };

  const handleSelectNode = async (nodeName) => {
    setSelectedNode(nodeName);
    setLoadingDeps(true);
    try {
      const res = await knowledgeAPI.graphDependencies(nodeName, 3, 'both');
      setDeps(res.data);
    } catch { toast.error('Error cargando dependencias'); }
    finally { setLoadingDeps(false); }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res = await knowledgeAPI.graphValidate();
      setValidation(res.data);
    } catch { toast.error('Error validando'); }
    finally { setValidating(false); }
  };

  const relColor = (rel) => {
    const m = { CALLS: 'text-blue-600 bg-blue-50', IMPORTS: 'text-green-600 bg-green-50', SELECT: 'text-cyan-600 bg-cyan-50', INSERT: 'text-amber-600 bg-amber-50', UPDATE: 'text-orange-600 bg-orange-50', DELETE: 'text-red-600 bg-red-50', FK_REFERENCES: 'text-purple-600 bg-purple-50', MENTIONS: 'text-pink-600 bg-pink-50', REFERENCES: 'text-fuchsia-600 bg-fuchsia-50' };
    return m[rel] || 'text-zinc-600 bg-zinc-50';
  };

  const typeIcon = (type) => {
    const t = (type || '').toUpperCase();
    if (t === 'TABLE') return <Database className="w-3 h-3 text-cyan-500" />;
    if (t === 'FORM') return <FileText className="w-3 h-3 text-amber-500" />;
    if (t === 'CLASS' || t === 'STRUCT') return <Box className="w-3 h-3 text-blue-500" />;
    if (t === 'FUNCTION' || t === 'METHOD') return <Zap className="w-3 h-3 text-green-500" />;
    if (t === 'INTERFACE' || t === 'ENUM') return <Hash className="w-3 h-3 text-purple-500" />;
    if (t.includes('PACKAGE')) return <FileCode className="w-3 h-3 text-indigo-500" />;
    if (t.startsWith('DOC_')) return <FileText className="w-3 h-3 text-red-500" />;
    if (t.startsWith('ENTITY_')) return <Network className="w-3 h-3 text-pink-500" />;
    return <GitBranch className="w-3 h-3 text-zinc-400" />;
  };

  if (loading) return <Skeleton className="h-[300px]" />;

  return (
    <div data-testid="graph-panel" className="space-y-4">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-900 space-y-1">
        <p className="font-semibold text-amber-950">Qué muestra este mapa</p>
        <p>
          Dependencias de <b>código y DDL</b> (packages, tablas, <span className="font-mono">FK_REFERENCES</span>, calls).
          Documentación Apex / árboles semánticos <span className="font-mono">.md</span> <b>no</b> aparecen como nodos aquí:
          se exploran con el harness KB (<span className="font-mono">kb_list_dir</span> / RAG) en chat o Playground.
        </p>
        <p className="text-amber-800/90">
          El mapa visual prioriza tablas y FKs dentro del límite de nodos; si hay miles de objetos, algunos nodos de código pueden omitirse.
        </p>
      </div>
      {/* Graph Stats */}
      {stats && stats.total_nodes > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Nodos" value={stats.total_nodes} icon={Network} color="text-purple-500" />
            <StatCard label="Edges" value={stats.total_edges} icon={ArrowRightLeft} color="text-brand-primary" />
            <StatCard label="Lenguajes" value={Object.keys(stats.nodes_by_language || {}).length} icon={FileCode} color="text-brand-success" />
            <StatCard label="Tipos" value={Object.keys(stats.nodes_by_type || {}).length} icon={Box} color="text-brand-warning" />
          </div>

          {/* Breakdown badges */}
          <div className="flex flex-wrap gap-2">
            {stats.nodes_by_language && Object.entries(stats.nodes_by_language).map(([lang, count]) => (
              <Badge key={lang} variant="outline" className="text-[9px] rounded-sm capitalize gap-1">
                <FileCode className="w-2.5 h-2.5" /> {lang}: {count}
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.nodes_by_type && Object.entries(stats.nodes_by_type).map(([type, count]) => (
              <Badge key={type} variant="outline" className="text-[9px] rounded-sm gap-1">
                {typeIcon(type)} {type}: {count}
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.edges_by_relation && Object.entries(stats.edges_by_relation).map(([rel, count]) => (
              <Badge key={rel} className={`text-[9px] rounded-sm border-0 ${relColor(rel)}`}>
                {rel}: {count}
              </Badge>
            ))}
          </div>
        </>
      ) : (
        <div className="p-6 border border-dashed border-zinc-200 rounded-sm text-center">
          <GitBranch className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No hay grafo de dependencias construido</p>
          <p className="text-[10px] text-zinc-400 mt-1">Escanea archivos primero, o construye el grafo manualmente</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <Button data-testid="build-graph-btn" onClick={handleBuild} disabled={building} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm text-xs h-8 gap-1">
          {building ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitBranch className="w-3 h-3" />}
          {building ? 'Construyendo...' : 'Reconstruir Grafo'}
        </Button>
        <Button data-testid="extract-docs-btn" onClick={async () => {
          setExtractingDocs(true);
          try {
            const res = await knowledgeAPI.graphExtractDocs();
            toast.success(`Metadata extraida: ${res.data.extracted} docs procesados, ${res.data.skipped} omitidos`);
            loadStats();
          } catch { toast.error('Error extrayendo metadata'); }
          finally { setExtractingDocs(false); }
        }} disabled={extractingDocs} variant="outline" size="sm" className="rounded-sm text-xs h-8 gap-1">
          {extractingDocs ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}
          {extractingDocs ? 'Extrayendo...' : 'Extraer Metadata Docs'}
        </Button>
        <Button data-testid="validate-graph-btn" onClick={handleValidate} disabled={validating} variant="outline" size="sm" className="rounded-sm text-xs h-8 gap-1">
          {validating ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
          Validar
        </Button>
        <Button data-testid="toggle-viz-btn" onClick={() => setShowViz(!showViz)} variant="outline" size="sm" className="rounded-sm text-xs h-8 gap-1 ml-auto">
          <Network className="w-3 h-3" />
          {showViz ? 'Ocultar Mapa' : 'Ver Mapa Visual'}
        </Button>
      </div>

      {/* Interactive Visualization */}
      {showViz && (
        <GraphVisualization focus="tables" />
      )}

      {/* Validation Results */}
      {validation && (
        <Card className="border border-zinc-200 rounded-sm" data-testid="graph-validation">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> Validacion del Grafo
              <Badge className={`text-[9px] rounded-sm border-0 ml-2 ${validation.health_score >= 80 ? 'bg-green-100 text-green-700' : validation.health_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                Salud: {validation.health_score}%
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div><span className="text-zinc-400">Nodos:</span> <b>{validation.total_nodes}</b></div>
              <div><span className="text-zinc-400">Edges:</span> <b>{validation.total_edges}</b></div>
              <div><span className="text-zinc-400">Huerfanos:</span> <b className={validation.orphan_count > 0 ? 'text-amber-600' : ''}>{validation.orphan_count}</b></div>
              <div><span className="text-zinc-400">Fantasmas:</span> <b className={validation.phantom_count > 0 ? 'text-red-600' : ''}>{validation.phantom_count}</b></div>
            </div>
            {validation.most_connected?.length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-400 mb-1">Mas conectados:</p>
                <div className="flex flex-wrap gap-1">
                  {validation.most_connected.slice(0, 10).map(n => (
                    <Badge
                      key={n.name}
                      variant="outline"
                      className="text-[9px] rounded-sm cursor-pointer hover:bg-purple-50"
                      onClick={() => handleSelectNode(n.name)}
                    >
                      {n.name} ({n.connections})
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {validation.phantom_targets?.length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-400 mb-1">Targets fantasma (referenciados pero sin nodo):</p>
                <div className="flex flex-wrap gap-1">
                  {validation.phantom_targets.slice(0, 15).map(p => (
                    <Badge key={p} className="text-[9px] rounded-sm bg-red-50 text-red-600 border-0">{p}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card className="border border-zinc-200 rounded-sm" data-testid="graph-search">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-1.5">
            <Search className="w-3 h-3" /> Explorar Dependencias
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              data-testid="graph-search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar objeto (ej: PKG_STOCK, UserService, app.py)..."
              className="flex-1 rounded-sm text-xs h-8"
            />
            <Button type="submit" disabled={searching} size="sm" variant="outline" className="rounded-sm text-xs h-8 gap-1">
              {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              Buscar
            </Button>
          </form>

          {/* Search Results */}
          {searchResults && (
            <div className="space-y-1">
              {searchResults.length === 0 ? (
                <p className="text-xs text-zinc-400 text-center py-2">Sin resultados</p>
              ) : searchResults.map((n, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between py-1.5 px-2 rounded-sm cursor-pointer transition-colors ${selectedNode === n.name ? 'bg-purple-50 border border-purple-200' : 'hover:bg-zinc-50 border border-transparent'}`}
                  onClick={() => handleSelectNode(n.name)}
                  data-testid={`graph-node-${n.name}`}
                >
                  <div className="flex items-center gap-2">
                    {typeIcon(n.type)}
                    <span className="text-xs font-medium text-[#09090B]">{n.name}</span>
                    <Badge variant="outline" className="text-[9px] rounded-sm">{n.type}</Badge>
                    {n.language && <Badge variant="outline" className="text-[9px] rounded-sm capitalize">{n.language}</Badge>}
                  </div>
                  <ChevronRight className="w-3 h-3 text-zinc-300" />
                </div>
              ))}
            </div>
          )}

          {/* Dependencies View */}
          {loadingDeps && <Skeleton className="h-[200px]" />}
          {deps && !loadingDeps && (
            <Card className="border border-purple-200 rounded-sm bg-purple-50/20" data-testid="dependency-viewer">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-purple-600 flex items-center gap-1.5">
                  <Eye className="w-3 h-3" /> {deps.root}
                  <span className="text-[9px] text-purple-400 font-normal">
                    {deps.total_nodes} nodos, {deps.total_edges} edges (profundidad {deps.depth_reached})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                {/* Nodes */}
                <div className="space-y-1">
                  {deps.nodes?.map((n, i) => (
                    <div key={i} className="flex items-center gap-2 py-1 px-2 rounded-sm bg-white border border-zinc-100">
                      {typeIcon(n.type)}
                      <span className={`text-xs font-medium ${n.name === deps.root ? 'text-purple-700' : 'text-[#09090B]'}`}>{n.name}</span>
                      <Badge variant="outline" className="text-[9px] rounded-sm">{n.type}</Badge>
                      {n.language && <Badge variant="outline" className="text-[9px] rounded-sm capitalize">{n.language}</Badge>}
                      <span className="text-[9px] text-zinc-400 ml-auto truncate max-w-[200px]">{n.file_path}</span>
                    </div>
                  ))}
                </div>
                {/* Edges */}
                {deps.edges?.length > 0 && (
                  <div className="space-y-1 mt-2">
                    <p className="text-[10px] text-zinc-400 font-semibold uppercase">Relaciones:</p>
                    {deps.edges.map((e, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] font-mono py-0.5">
                        <span className="text-zinc-700">{e.source}</span>
                        <Badge className={`text-[8px] rounded-sm border-0 ${relColor(e.relation)}`}>{e.relation}</Badge>
                        <ArrowRight className="w-2.5 h-2.5 text-zinc-300" />
                        <span className="text-zinc-700">{e.target}</span>
                        {e.target_method && <span className="text-zinc-400">.{e.target_method}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── KB Config Panel (tenant + override agente/capsula) ──
const KB_COLLECTION_OPTIONS = ['code', 'docs', 'tickets', 'artifacts'];

function KBConfigPanel({ user, initialCapsuleId = '', initialAgentId = '' }) {
  const [loading, setLoading] = useState(true);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [globalCfg, setGlobalCfg] = useState(null);
  const [capsules, setCapsules] = useState([]);
  const [agents, setAgents] = useState([]);
  const [capsuleId, setCapsuleId] = useState(initialCapsuleId || '');
  const [agentId, setAgentId] = useState(initialAgentId || '');
  const [agentCfg, setAgentCfg] = useState({});

  const loadGlobal = useCallback(async () => {
    const res = await knowledgeAPI.getConfig();
    setGlobalCfg(res.data?.config || {});
  }, []);

  const loadCapsules = useCallback(async () => {
    if (!user?.tenant_id) return;
    const res = await capsulesAPI.list(user.tenant_id);
    const list = res.data || [];
    setCapsules(list);
    if (!capsuleId && list.length > 0) setCapsuleId(initialCapsuleId || list[0].capsule_id);
  }, [user?.tenant_id, capsuleId, initialCapsuleId]);

  const loadAgents = useCallback(async () => {
    if (!user?.tenant_id || !capsuleId) return;
    const res = await agentsAPI.list(user.tenant_id, capsuleId);
    const list = res.data || [];
    setAgents(list);
    if (!agentId && list.length > 0) setAgentId(initialAgentId || list[0].agent_id);
  }, [user?.tenant_id, capsuleId, agentId, initialAgentId]);

  const loadAgentCfg = useCallback(async () => {
    if (!capsuleId || !agentId) return;
    const res = await knowledgeAPI.getAgentConfig(capsuleId, agentId);
    setAgentCfg(res.data?.config || {});
  }, [capsuleId, agentId]);

  useEffect(() => {
    (async () => {
      try {
        await loadGlobal();
        await loadCapsules();
      } catch {
        toast.error('Error cargando configuracion KB');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadGlobal, loadCapsules]);

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadAgentCfg(); }, [loadAgentCfg]);

  const saveGlobal = async () => {
    setSavingGlobal(true);
    try {
      const payload = { ...(globalCfg || {}) };
      // Stubs: no editables desde UI (no afectan retrieval real hoy)
      delete payload.context_budget_tokens;
      delete payload.retrieval_mode;
      await knowledgeAPI.updateConfig(payload);
      toast.success('Configuracion global KB guardada');
      await loadGlobal();
    } catch {
      toast.error('No se pudo guardar la configuracion global');
    } finally {
      setSavingGlobal(false);
    }
  };

  const saveOverride = async () => {
    if (!capsuleId || !agentId) return;
    setSavingOverride(true);
    try {
      const payload = { ...(agentCfg || {}) };
      delete payload.context_budget_tokens;
      delete payload.retrieval_mode;
      await knowledgeAPI.updateAgentConfig(capsuleId, agentId, payload);
      toast.success('Override por agente guardado');
      await loadAgentCfg();
    } catch {
      toast.error('No se pudo guardar override de agente');
    } finally {
      setSavingOverride(false);
    }
  };

  if (loading) return <Skeleton className="h-[280px]" />;

  return (
    <div className="space-y-4" data-testid="kb-config-panel">
      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
            Configuracion global KB (tenant)
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-3">
          <KbInlineHelp defaultOpen>
            <p><b>max_results / min_score:</b> controlan cuántos hits Qdrant entran al prompt y el umbral de score.</p>
            <p><b>rerank / use_graph_enrichment:</b> post-procesan resultados (rerank) o enriquecen con vecinos del grafo DDL/código.</p>
            <p><b>citation_instructions:</b> texto inyectado al agente para citar fuentes.</p>
            <p className="text-zinc-500">Los stubs <code>retrieval_mode</code> y <code>context_budget_tokens</code> se quitaron de la UI: hoy no cambian el retrieval real (solo se loguean).</p>
          </KbInlineHelp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Max resultados</Label>
              <Input type="number" className="h-8 text-xs rounded-sm" value={globalCfg?.max_results ?? 8}
                onChange={(e) => setGlobalCfg((p) => ({ ...p, max_results: Number(e.target.value) || 8 }))} />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Min score</Label>
              <Input type="number" step="0.01" className="h-8 text-xs rounded-sm" value={globalCfg?.min_score ?? 0.15}
                onChange={(e) => setGlobalCfg((p) => ({ ...p, min_score: Number(e.target.value) || 0.15 }))} />
            </div>
          </div>
          <div className="flex items-center gap-5">
            <label className="text-xs text-zinc-600 flex items-center gap-2">
              <input type="checkbox" checked={!!globalCfg?.rerank}
                onChange={(e) => setGlobalCfg((p) => ({ ...p, rerank: e.target.checked }))} />
              rerank habilitado
            </label>
            <label className="text-xs text-zinc-600 flex items-center gap-2">
              <input type="checkbox" checked={!!globalCfg?.use_graph_enrichment}
                onChange={(e) => setGlobalCfg((p) => ({ ...p, use_graph_enrichment: e.target.checked }))} />
              usar grafo por defecto
            </label>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Instrucciones de cita KB</Label>
            <Textarea className="text-xs rounded-sm min-h-[70px]"
              value={globalCfg?.citation_instructions || ''}
              onChange={(e) => setGlobalCfg((p) => ({ ...p, citation_instructions: e.target.value }))} />
          </div>
          <Button size="sm" className="h-8 text-xs bg-brand-primary text-white" onClick={saveGlobal} disabled={savingGlobal}>
            {savingGlobal ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Guardar global
          </Button>
        </CardContent>
      </Card>

      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
            Override por agente/capsula
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select className="h-8 text-xs rounded-sm border border-zinc-200 px-2"
              value={capsuleId} onChange={(e) => { setCapsuleId(e.target.value); setAgentId(''); }}>
              {capsules.map((c) => <option key={c.capsule_id} value={c.capsule_id}>{c.capsule_id}</option>)}
            </select>
            <select className="h-8 text-xs rounded-sm border border-zinc-200 px-2"
              value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              {agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.agent_id}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="max_results" type="number" className="h-8 text-xs rounded-sm"
              value={agentCfg?.max_results ?? ''} onChange={(e) => setAgentCfg((p) => ({ ...p, max_results: e.target.value === '' ? null : Number(e.target.value) }))} />
            <Input placeholder="min_score" type="number" step="0.01" className="h-8 text-xs rounded-sm"
              value={agentCfg?.min_score ?? ''} onChange={(e) => setAgentCfg((p) => ({ ...p, min_score: e.target.value === '' ? null : Number(e.target.value) }))} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
            <Input placeholder="kb_answer_model_ref (opcional)" className="h-8 text-xs rounded-sm font-mono"
              value={agentCfg?.kb_answer_model_ref ?? ''} onChange={(e) => setAgentCfg((p) => ({ ...p, kb_answer_model_ref: e.target.value }))} />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="text-xs text-zinc-600 flex items-center gap-2">
              <input type="checkbox" checked={!!agentCfg?.use_graph_enrichment}
                onChange={(e) => setAgentCfg((p) => ({ ...p, use_graph_enrichment: e.target.checked }))} />
              use_graph_enrichment
            </label>
            <label className="text-xs text-zinc-600 flex items-center gap-2">
              <input type="checkbox" checked={agentCfg?.rerank !== false}
                onChange={(e) => setAgentCfg((p) => ({ ...p, rerank: e.target.checked }))} />
              rerank
            </label>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Colecciones</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              {KB_COLLECTION_OPTIONS.map((c) => (
                <label key={c} className="text-xs flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={(agentCfg?.collection_types || KB_COLLECTION_OPTIONS).includes(c)}
                    onChange={() => {
                      const current = agentCfg?.collection_types || KB_COLLECTION_OPTIONS;
                      const next = current.includes(c) ? current.filter((x) => x !== c) : [...current, c];
                      setAgentCfg((p) => ({ ...p, collection_types: next.length ? next : ['docs'] }));
                    }}
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={saveOverride} disabled={savingOverride || !agentId}>
            {savingOverride ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Guardar override agente
          </Button>
        </CardContent>
      </Card>

      <Card className="border border-zinc-200 rounded-sm bg-zinc-50">
        <CardContent className="px-5 py-4 text-xs text-zinc-600 space-y-1">
          <p className="font-semibold text-zinc-700">Guia rapida de configuracion</p>
          <p><b>Soporte N1 conservador:</b> min_score=0.25, max_results=5, use_graph_enrichment=false.</p>
          <p><b>Soporte N2 tecnico:</b> min_score=0.12, max_results=12, use_graph_enrichment=true.</p>
          <p><b>Consultas funcionales:</b> limitar colecciones a docs/tickets en override del agente.</p>
          <p><b>Opcion B:</b> define <code>kb_answer_model_ref</code> solo en agentes que necesitan mejor sintesis tecnica.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Page ──
export default function KnowledgePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'playground';
  const initialCapsule = searchParams.get('capsule') || '';
  const initialAgent = searchParams.get('agent') || '';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [playgroundSeed, setPlaygroundSeed] = useState({ capsule: initialCapsule, query: searchParams.get('q') || '' });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const res = await knowledgeAPI.stats();
      setStats(res.data);
    } catch {
      /* empty */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const onUpdate = () => loadStats();
    window.addEventListener('kb-index-updated', onUpdate);
    return () => window.removeEventListener('kb-index-updated', onUpdate);
  }, [loadStats]);

  return (
    <div className="p-6 max-w-[1440px] mx-auto" data-testid="knowledge-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-[#09090B] tracking-tight">
          Base de Conocimiento
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          RAG con embeddings semanticos, grafo de dependencias multi-lenguaje, y analisis profundo
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="kb-stats">
        <StatCard label="Archivos" value={stats?.total_files ?? '-'} icon={FileText} color="text-brand-primary" />
        <StatCard label="Chunks" value={stats?.total_chunks ?? '-'} icon={Database} color="text-brand-success" />
        <StatCard label="Codigo" value={stats?.collections?.code?.points_count ?? 0} icon={FileCode} color="text-brand-primary" />
        <StatCard label="Docs" value={stats?.collections?.docs?.points_count ?? 0} icon={FileType} color="text-brand-warning" />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="kb-tabs">
        <TabsList className="bg-zinc-100 rounded-sm h-9">
          <TabsTrigger value="playground" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-playground">
            Playground
          </TabsTrigger>
          <TabsTrigger value="graph" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-graph">
            <GitBranch className="w-3 h-3 mr-1" /> Grafo
          </TabsTrigger>
          <TabsTrigger value="content" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-content">
            <FolderOpen className="w-3 h-3 mr-1" /> Contenido
          </TabsTrigger>
          <TabsTrigger value="origins" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-origins">
            <HardDrive className="w-3 h-3 mr-1" /> Origenes
          </TabsTrigger>
          <TabsTrigger value="config" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-config">
            <SlidersHorizontal className="w-3 h-3 mr-1" /> Configuracion
          </TabsTrigger>
          <TabsTrigger value="memory" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-memory">
            <BookOpen className="w-3 h-3 mr-1" /> Memoria
          </TabsTrigger>
          <TabsTrigger value="help" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-help">
            <HelpCircle className="w-3 h-3 mr-1" /> Ayuda
          </TabsTrigger>
        </TabsList>

        <TabsContent value="playground" className="mt-4">
          <PlaygroundPanel
            user={user}
            seedCapsule={playgroundSeed.capsule}
            seedQuery={playgroundSeed.query}
          />
        </TabsContent>

        <TabsContent value="graph" className="mt-4">
          <GraphPanel />
        </TabsContent>

        <TabsContent value="content" className="mt-4">
          <KBContentPanel user={user} />
        </TabsContent>

        <TabsContent value="origins" className="mt-4">
          <KBOriginsPanel />
        </TabsContent>

        <TabsContent value="config" className="mt-4">
          <KBConfigPanel user={user} initialCapsuleId={initialCapsule} initialAgentId={initialAgent} />
        </TabsContent>

        <TabsContent value="memory" className="mt-4">
          <OrgMemoryObservatoryPanel
            user={user}
            initialCapsuleId={initialCapsule}
            onProbeInPlayground={(capsuleId, query) => {
              setPlaygroundSeed({ capsule: capsuleId, query });
              setActiveTab('playground');
            }}
          />
        </TabsContent>

        <TabsContent value="help" className="mt-4">
          <KBHelpPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
